import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, ViewEncapsulation } from '@angular/core';

import { MatSlideToggleChange } from '@angular/material/slide-toggle';

import { ABTest } from '../../../newsletters-module/declarations/ab-test';
import { ABTestStatus, getABTestStatus } from '../../../newsletters-module/views/ab-test/ab-test.utils';

/**
 * Toggle "édition Test AB" réutilisé dans plusieurs toolbars : bascule l'édition d'un panneau depuis la source
 * live vers la variante B d'un test A/B en cours ou planifié (un test terminé n'est plus proposé), avec un
 * sélecteur pour choisir lequel parmi ceux disponibles.
 *
 * Composant purement présentationnel : il ne connaît ni le state global ni les endpoints de sauvegarde, seulement
 * la liste des tests proposables et la sélection courante — à chaque panneau consommateur de propager la sélection
 * et d'adapter son propre flux de chargement/sauvegarde. Il calcule néanmoins lui-même le statut de chaque test
 * pour afficher le badge par option et l'avertissement quand le test sélectionné est déjà en cours : la variante
 * éditée est alors potentiellement déjà servie au trafic live.
 */
@Component({
  selector: 'app-mediego-ab-test-editing-toggle',
  templateUrl: './ab-test-editing-toggle.component.html',
  styleUrls: ['./ab-test-editing-toggle.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
export class AbTestEditingToggleComponent {
  @Input() editableTests: ABTest[] = [];
  @Input() selectedTest: ABTest | null = null;

  @Output() testSelected = new EventEmitter<ABTest | null>();

  get enabled(): boolean {
    return !!this.selectedTest;
  }

  get disabled(): boolean {
    return !this.editableTests?.length;
  }

  // Avertissement affiché quand le test édité est déjà en cours : la modification s'applique à la variante B telle
  // que servie au trafic live, contrairement à un test `scheduled` pas encore démarré. Cas rare en pratique (une
  // campagne s'exécute en général une fois par jour, en quelques minutes) mais à signaler explicitement.
  get selectedTestIsRunning(): boolean {
    return !!this.selectedTest && this.getStatus(this.selectedTest) === 'running';
  }

  getStatus = (test: ABTest): ABTestStatus => getABTestStatus(test);

  compareTests = (a: ABTest, b: ABTest): boolean => a?.id?.id === b?.id?.id;

  changeEnabled(event: MatSlideToggleChange): void {
    // à l'activation, sélection par défaut du test le plus proche (le 1er de la liste, triée par date de début
    // croissante, donc un test déjà en cours remonte avant les tests planifiés) ; à la désactivation, retour à
    // l'édition de la source live.
    this.testSelected.emit(event.checked ? this.editableTests[0] : null);
  }

  changeSelectedTest(test: ABTest): void {
    this.testSelected.emit(test);
  }
}
